import { useCallback, useMemo, useState } from 'react';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import { useStableQuery } from '../../db/useStableQuery';
import { FormattedMessage, useIntl } from 'react-intl';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import type { DiaryDocType } from '@ast-data/shared';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import { formatDate, formatWeekdayDate } from '../../utils/formatDate';
import { netMinutes, formatHoursMinutes } from '../timeTracking/timeMath';
import FieldCardList, { type FieldCardRow } from './FieldCardList';
import type { FieldSortOption } from './fieldListSort';

// Newest day first — the one being worked on is usually the latest.
const SORT = {
    listId: 'diaries',
    options: [
        { key: 'date', labelId: 'field.sort.date', direction: 'desc' }
    ] satisfies FieldSortOption[]
};
import { useShownJson } from '../users/personLabels';

/**
 * Touch-first list of a project's Tagebuch days, with fast add. Selecting a day
 * (or Add) opens the office Tagebuch fullscreen dialog over this list, like the
 * Befunde category — see [[field-reuses-office-dialogs]].
 */
export default function FieldDiaryPage() {
    const intl = useIntl();
    const navigate = useNavigate();
    const { projectId = '' } = useParams<{ projectId: string }>();

    const diariesQuery = useStableQuery<DiaryDocType>({
        selector: { projectId }
    });
    const { results: diariesDocs } = useLiveRxQuery<DiaryDocType>({
        collection: 'diaries',
        query: diariesQuery
    });
    const diaries = useShownJson<DiaryDocType>('diary', diariesDocs);

    const ability = useAbility();
    const canCreate = ability.can(
        'create',
        scopedSubject('Diary', { projectId })
    );

    const [search, setSearch] = useState('');

    const rows = useMemo<FieldCardRow[]>(
        () =>
            diaries.map((diary) => {
                // Net worked time of the day (Arbeitszeit minus Pause); blank
                // when the start/end aren't both set.
                const worked = formatHoursMinutes(
                    netMinutes(
                        diary.workStart,
                        diary.workEnd,
                        Number(diary.breakTime) || 0
                    )
                );
                // The list is picked from in the field, where the weekday and
                // the calendar week place a day faster than its date alone.
                const day = formatWeekdayDate(diary.recordedAt);
                return {
                    id: diary.id,
                    title: day || intl.formatMessage({ id: 'diary.title' }),
                    subtitle: diary.author || undefined,
                    trailing: worked ? (
                        <Typography
                            variant="subtitle1"
                            sx={{ color: 'text.secondary', flexShrink: 0 }}
                        >
                            <FormattedMessage
                                id="diary.workedHours"
                                values={{ hours: worked }}
                            />
                        </Typography>
                    ) : undefined,
                    sortKey: diary.recordedAt || '',
                    sortValues: { date: diary.recordedAt || '' },
                    /**
                     * The German date as shown, so searching for "29.07" (or
                     * "Montag", or "KW 24") finds the day the list is labelled
                     * by rather than its ISO form.
                     */
                    searchText: [
                        day,
                        formatDate(diary.recordedAt),
                        diary.author
                    ]
                        .filter(Boolean)
                        .join(' ')
                };
            }),
        [diaries, intl]
    );

    const handleSelect = useCallback(
        (id: string) => navigate(`/field/${projectId}/diaries/${id}/edit`),
        [navigate, projectId]
    );

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <FieldCardList
                rows={rows}
                onSelect={handleSelect}
                emptyMessageId="field.diaries.empty"
                noMatchesMessageId="field.diaries.noMatches"
                sort={SORT}
                search={search}
                onSearchChange={setSearch}
                toolbar={
                    canCreate ? (
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<AddIcon />}
                            onClick={() =>
                                navigate(`/field/${projectId}/diaries/new`)
                            }
                        >
                            <FormattedMessage id="diary.add" />
                        </Button>
                    ) : undefined
                }
            />
            {/* The Tagebuch dialog routes render here, fullscreen over the list. */}
            <Outlet />
        </Box>
    );
}
